import type {
  AnyExportedSpan,
  ExportedFeedback,
  FeedbackEvent,
  ModelGenerationAttributes,
  SpanErrorInfo,
  UsageStats,
} from '@mastra/core/observability';
import { SpanType } from '@mastra/core/observability';
import type { TraceData, TrackingExporterConfig } from '@mastra/observability';
import { TrackingExporter } from '@mastra/observability';
import { PostHog } from 'posthog-node';
import type { EventMessage } from 'posthog-node';
import { isModelInferenceEnabled } from './features';

/**
 * Token usage format compatible with PostHog.
 * @see https://posthog.com/docs/llm-analytics/generations#event-properties
 */
export interface PostHogUsageMetrics {
  $ai_input_tokens?: number;
  $ai_output_tokens?: number;
  $ai_cache_read_input_tokens?: number;
  $ai_cache_creation_input_tokens?: number;
  $ai_cache_creation_5m_input_tokens?: number;
  $ai_cache_creation_1h_input_tokens?: number;
}

/**
 * Formats UsageStats to PostHog's expected property format.
 *
 * Pass through gross input token counts with cache fields as subsets.
 * PostHog subtracts cache tokens when computing costs for non-Anthropic
 * providers and detects Anthropic-style exclusive reporting on its own.
 *
 * @param usage - The UsageStats from span attributes
 * @returns PostHog-formatted usage properties
 */
export function formatUsageMetrics(usage?: UsageStats): PostHogUsageMetrics {
  if (!usage) return {};

  const props: PostHogUsageMetrics = {};

  if (usage.inputTokens !== undefined) {
    props.$ai_input_tokens = usage.inputTokens;
  }

  if (usage.inputDetails?.cacheRead !== undefined) {
    props.$ai_cache_read_input_tokens = usage.inputDetails.cacheRead;
  }

  if (usage.inputDetails?.cacheWrite !== undefined) {
    props.$ai_cache_creation_input_tokens = usage.inputDetails.cacheWrite;
  }
  if (usage.inputDetails?.cacheWrite5m !== undefined) {
    props.$ai_cache_creation_5m_input_tokens = usage.inputDetails.cacheWrite5m;
  }
  if (usage.inputDetails?.cacheWrite1h !== undefined) {
    props.$ai_cache_creation_1h_input_tokens = usage.inputDetails.cacheWrite1h;
  }

  if (usage.outputTokens !== undefined) {
    props.$ai_output_tokens = usage.outputTokens;
  }

  return props;
}

interface PostHogMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: PostHogContent[];
}

interface PostHogContent {
  type: string;
  text?: string;
  [key: string]: unknown;
}

interface MastraMessage {
  role: string;
  content: string | MastraContent[];
}

interface MastraContent {
  type: string;
  text?: string;
  [key: string]: unknown;
}

type SpanData = string | MastraMessage[] | Record<string, unknown> | unknown;

const DISTINCT_ID = 'distinctId';
/** Per-trace key prefix for the provider calls and tool results recorded under one MODEL_GENERATION. */
const CONVERSATION = 'conversation:';

/** Ended spans under one MODEL_GENERATION, used to rebuild each provider call's `$ai_input`. */
interface Conversation {
  /** Ended MODEL_INFERENCE spans, in the order they ended. */
  calls: AnyExportedSpan[];
  /** Ended TOOL_CALL / MCP_TOOL_CALL spans and PROVIDER_TOOL_CALL spans with a result, keyed by toolCallId. */
  toolResults: Map<string, AnyExportedSpan>;
}

export interface PosthogExporterConfig extends TrackingExporterConfig {
  /** PostHog API key. Defaults to POSTHOG_API_KEY environment variable. */
  apiKey?: string;
  /** PostHog host URL. Defaults to POSTHOG_HOST environment variable or US region. */
  host?: string;
  flushAt?: number;
  flushInterval?: number;
  serverless?: boolean;
  defaultDistinctId?: string;
  enablePrivacyMode?: boolean;
}

type PosthogRoot = unknown;
type PosthogSpan = AnyExportedSpan;
// used as a placeholder for event data since we don't need to cache
// event data for Posthog
type PosthogEvent = boolean;
type PosthogMetadata = unknown;
type PosthogTraceData = TraceData<PosthogRoot, PosthogSpan, PosthogEvent, PosthogMetadata>;

export class PosthogExporter extends TrackingExporter<
  PosthogRoot,
  PosthogSpan,
  PosthogEvent,
  PosthogMetadata,
  PosthogExporterConfig
> {
  name = 'posthog';
  #client: PostHog | undefined;

  private static readonly SERVERLESS_FLUSH_AT = 10;
  private static readonly SERVERLESS_FLUSH_INTERVAL = 2000;
  private static readonly DEFAULT_FLUSH_AT = 20;
  private static readonly DEFAULT_FLUSH_INTERVAL = 10000;

  constructor(config: PosthogExporterConfig = {}) {
    // Resolve env vars BEFORE calling super (config is readonly in base class)
    const apiKey = config.apiKey ?? process.env.POSTHOG_API_KEY;

    super({ ...config, apiKey });

    if (!apiKey) {
      this.setDisabled('Missing required API key. Set POSTHOG_API_KEY environment variable or pass apiKey in config.');
      return;
    }

    const clientConfig = this.buildClientConfig(this.config);
    this.#client = new PostHog(apiKey, clientConfig);
    const message =
      (config.serverless ?? false) ? 'PostHog exporter initialized in serverless mode' : 'PostHog exporter initialized';
    this.logger.debug(message, config);
  }

  private buildClientConfig(config: PosthogExporterConfig) {
    const isServerless = config.serverless ?? false;
    const flushAt =
      config.flushAt ?? (isServerless ? PosthogExporter.SERVERLESS_FLUSH_AT : PosthogExporter.DEFAULT_FLUSH_AT);
    const flushInterval =
      config.flushInterval ??
      (isServerless ? PosthogExporter.SERVERLESS_FLUSH_INTERVAL : PosthogExporter.DEFAULT_FLUSH_INTERVAL);

    const host = config.host || process.env.POSTHOG_HOST || 'https://us.i.posthog.com';

    if (!config.host && !process.env.POSTHOG_HOST) {
      this.logger.info(
        'No PostHog host specified, using US default (https://us.i.posthog.com). ' +
          'For EU region, set `host: "https://eu.i.posthog.com"` in config or POSTHOG_HOST env var. ' +
          'For self-hosted, provide your instance URL.',
      );
    }

    return {
      host,
      flushAt,
      flushInterval,
      privacyMode: config.enablePrivacyMode,
    };
  }

  protected override skipBuildRootTask = true;
  protected override async _buildRoot(_args: {
    span: AnyExportedSpan;
    traceData: PosthogTraceData;
  }): Promise<PosthogRoot | undefined> {
    throw new Error('Method not implemented.');
  }

  protected override skipCachingEventSpans = true;
  protected override async _buildEvent(args: {
    span: AnyExportedSpan;
    traceData: PosthogTraceData;
  }): Promise<PosthogEvent> {
    const { span, traceData } = args;

    const isModelCall = this.isModelCall(span);
    const distinctId = this.getDistinctId(span, traceData);
    const properties = this.buildEventProperties(span, 0, false, isModelCall);

    // captureAi() targets PostHog's dedicated AI endpoint (8 MiB per event, oversized events
    // dropped individually). Plain capture() uses /batch/, which returns 413 for large spans.
    this.#client?.captureAi(
      this.withGroups({
        distinctId,
        event: isModelCall ? '$ai_generation' : '$ai_span',
        properties,
        timestamp: span.endTime ? new Date(span.endTime) : new Date(),
      }),
    );

    return true;
  }

  protected override async _buildSpan(args: {
    span: AnyExportedSpan;
    traceData: PosthogTraceData;
  }): Promise<PosthogSpan | undefined> {
    const { span, traceData } = args;
    if (!traceData.hasExtraValue(DISTINCT_ID)) {
      const userId = span.metadata?.userId;
      if (userId) {
        traceData.setExtraValue(DISTINCT_ID, String(userId));
      }
    }

    return span;
  }

  protected override skipSpanUpdateEvents = true;
  protected override _updateSpan(_args: { span: AnyExportedSpan; traceData: PosthogTraceData }): Promise<void> {
    throw new Error('Method not implemented.');
  }

  protected override async _finishSpan(args: { span: AnyExportedSpan; traceData: PosthogTraceData }): Promise<void> {
    const { span, traceData } = args;

    // Merge input from cached span (SPAN_STARTED) if not present on end span
    // This handles the case where input is only sent at start
    const cachedSpan = traceData.getSpan({ spanId: span.id });
    const mergedSpan = !span.input && cachedSpan?.input ? { ...span, input: cachedSpan.input } : span;

    const eventMessage = this.buildEventMessage({ span: this.withCallInput(mergedSpan, traceData), traceData });
    this.#client?.captureAi(this.withGroups(eventMessage));

    this.recordForConversation(span, traceData);
  }

  protected override async _abortSpan(args: {
    span: PosthogSpan;
    reason: SpanErrorInfo;
    traceData: PosthogTraceData;
  }): Promise<void> {
    const { span, reason, traceData } = args;

    // update span with the abort reason
    span.errorInfo = reason;

    const eventMessage = this.buildEventMessage({ span: this.withCallInput(span, traceData), traceData });
    this.#client?.captureAi(this.withGroups(eventMessage));
  }

  /**
   * Forward feedback recorded via `addFeedback()` to PostHog as a native
   * `$ai_feedback` event, shown as "User feedback" on the linked trace in
   * PostHog's AI observability UI.
   */
  async onFeedbackEvent(event: FeedbackEvent): Promise<void> {
    if (!this.#client) return;

    const { feedback } = event;
    if (!feedback.traceId) {
      this.logger.debug('PostHog exporter: dropping feedback with no traceId; PostHog requires $ai_trace_id', {
        feedbackId: feedback.feedbackId,
      });
      return;
    }

    const properties: Record<string, any> = {
      // Custom metadata goes first so it cannot overwrite the natively mapped fields below
      ...this.extractCustomMetadata(feedback.metadata),
      $ai_trace_id: feedback.traceId,
      // PostHog's trace UI only displays feedback events that carry $ai_feedback_text
      $ai_feedback_text: feedback.comment ?? String(feedback.value),
      feedback_id: feedback.feedbackId,
      feedback_type: feedback.feedbackType,
      feedback_value: feedback.value,
    };

    const feedbackSource = feedback.feedbackSource ?? feedback.source;
    if (feedbackSource) properties.feedback_source = feedbackSource;
    if (feedback.spanId) properties.span_id = feedback.spanId;
    if (feedback.sourceId) properties.source_id = feedback.sourceId;
    if (feedback.metadata?.sessionId) properties.$ai_session_id = feedback.metadata.sessionId;

    try {
      this.#client.captureAi(
        this.withGroups({
          distinctId: this.getFeedbackDistinctId(feedback),
          event: '$ai_feedback',
          properties,
          timestamp: new Date(feedback.timestamp),
        }),
      );
    } catch (err) {
      this.logger.error('PostHog exporter: failed to submit feedback', {
        error: err,
        traceId: feedback.traceId,
        feedbackId: feedback.feedbackId,
      });
    }
  }

  private getFeedbackDistinctId(feedback: ExportedFeedback): string {
    const userId = feedback.feedbackUserId ?? feedback.userId ?? feedback.metadata?.userId;
    if (userId) {
      return String(userId);
    }
    return this.config.defaultDistinctId ?? 'anonymous';
  }

  /**
   * PostHog group analytics are keyed off the top-level `groups` field on the
   * capture call. The Node SDK derives the event's `$groups` from that field and
   * overwrites any property-level `$groups`, so group metadata carried in
   * properties is dropped unless it is mirrored here.
   */
  private withGroups(message: EventMessage): EventMessage {
    const groups = message.properties?.$groups;
    if (groups && typeof groups === 'object' && !Array.isArray(groups)) {
      return { ...message, groups };
    }
    return message;
  }

  private buildEventMessage(args: { span: AnyExportedSpan; traceData: PosthogTraceData }): EventMessage {
    const { span, traceData } = args;

    const endTime = span.endTime ? this.toDate(span.endTime).getTime() : Date.now();

    const distinctId = this.getDistinctId(span, traceData);

    if (span.isRootSpan) {
      return this.buildRootEventMessage({ span, distinctId, endTime });
    } else {
      return this.buildChildEventMessage({ span, distinctId, endTime, traceData });
    }
  }

  /**
   * Capture an explicit $ai_trace event for root spans.
   * This gives us control over trace-level metadata like name and tags,
   * rather than relying on PostHog's pseudo-trace auto-creation.
   */
  private buildRootEventMessage(args: { span: AnyExportedSpan; distinctId: string; endTime: number }): EventMessage {
    const { span, distinctId, endTime } = args;

    // Note: We don't set $ai_latency on $ai_trace events because PostHog
    // aggregates latency from child events. Setting it here causes double-counting.
    const traceProperties: Record<string, any> = {
      $ai_trace_id: span.traceId,
      $ai_span_name: span.name,
      $ai_is_error: !!span.errorInfo,
    };

    if (span.metadata?.sessionId) {
      traceProperties.$ai_session_id = span.metadata.sessionId;
    }

    if (span.input) {
      traceProperties.$ai_input_state = span.input;
    }

    if (span.output) {
      traceProperties.$ai_output_state = span.output;
    }

    if (span.errorInfo) {
      traceProperties.$ai_error = {
        message: span.errorInfo.message,
        ...(span.errorInfo.id && { id: span.errorInfo.id }),
        ...(span.errorInfo.category && { category: span.errorInfo.category }),
      };
    }

    // Add tags as custom properties (PostHog doesn't have native tag support on traces)
    if (span.tags?.length) {
      for (const tag of span.tags) {
        traceProperties[tag] = true;
      }
    }

    // Add custom metadata (excluding userId and sessionId which are handled separately)
    const { userId, sessionId, ...customMetadata } = span.metadata ?? {};
    Object.assign(traceProperties, customMetadata);

    // A served MCP request is always a root span. Keep the caller's W3C context
    // on the trace, since PostHog has no span links to point at the caller.
    if (span.type === SpanType.MCP_SERVER_REQUEST) {
      const { callerTraceparent, callerTracestate } = (span.attributes ?? {}) as {
        callerTraceparent?: string;
        callerTracestate?: string;
      };
      if (callerTraceparent) traceProperties.callerTraceparent = callerTraceparent;
      if (callerTracestate) traceProperties.callerTracestate = callerTracestate;
    }

    return {
      distinctId,
      event: '$ai_trace',
      properties: traceProperties,
      timestamp: new Date(endTime),
    };
  }

  private buildChildEventMessage(args: {
    span: AnyExportedSpan;
    distinctId: string;
    endTime: number;
    traceData: PosthogTraceData;
  }): EventMessage {
    const { span, distinctId, endTime, traceData } = args;

    const isModelCall = this.isModelCall(span);
    const startTime = span.startTime.getTime();
    const latency = (endTime - startTime) / 1000;

    // Check if parent is the root span - if so, use traceId as parent_id
    // since we don't create an $ai_span for root spans
    const parentIsRootSpan = this.isParentRootSpan(span, traceData);
    const properties = this.buildEventProperties(span, latency, parentIsRootSpan, isModelCall);

    return {
      distinctId,
      event: isModelCall ? '$ai_generation' : '$ai_span',
      properties,
      timestamp: new Date(endTime),
    };
  }

  private toDate(timestamp: Date | number): Date {
    return timestamp instanceof Date ? timestamp : new Date(timestamp);
  }

  /**
   * Whether this span is exported as `$ai_generation`. PostHog expects one per
   * provider call and sums tokens and cost across them, so exactly one span
   * per call may carry usage. With paired packages that emit MODEL_INFERENCE,
   * that is the call and MODEL_GENERATION is the loop around it, exported as
   * `$ai_span`. Older pairings only emit MODEL_GENERATION, which keeps the
   * `$ai_generation` role.
   */
  private isModelCall(span: AnyExportedSpan): boolean {
    return span.type === (isModelInferenceEnabled() ? SpanType.MODEL_INFERENCE : SpanType.MODEL_GENERATION);
  }

  /**
   * Give a MODEL_INFERENCE exported as `$ai_generation` the conversation that
   * call received. The span has no input of its own, so it is rebuilt from the
   * generation's input plus, for each earlier call in the same generation, its
   * output and the results of the tools it called (as Braintrust's Thread view
   * does). Earlier calls and their tools end before this call does, so they are
   * already recorded.
   */
  private withCallInput(span: AnyExportedSpan, traceData: PosthogTraceData): AnyExportedSpan {
    if (span.type !== SpanType.MODEL_INFERENCE || !this.isModelCall(span)) return span;

    const generation = this.findGeneration(span, traceData);
    if (!generation?.input) return span;

    const messages = this.formatMessages(generation.input, 'user');
    const conversation = traceData.getExtraValue(CONVERSATION + generation.id) as Conversation | undefined;
    for (const call of conversation?.calls ?? []) {
      const output = call.output as
        | { text?: string; toolCalls?: Array<{ toolCallId: string; toolName: string }> }
        | undefined;
      if (!output?.text && !output?.toolCalls?.length) continue;
      messages.push(...this.formatMessages(output, 'assistant'));

      // Parallel tool calls are ordered by when each tool started.
      const results = (output.toolCalls ?? [])
        .map(toolCall => ({ toolCall, toolSpan: conversation!.toolResults.get(toolCall.toolCallId) }))
        .filter(
          (result): result is { toolCall: typeof result.toolCall; toolSpan: AnyExportedSpan } => !!result.toolSpan,
        )
        .sort((a, b) => this.toDate(a.toolSpan.startTime).getTime() - this.toDate(b.toolSpan.startTime).getTime());
      for (const { toolCall, toolSpan } of results) {
        // Provider-executed tools report failure through `success`, not `errorInfo`.
        const isError =
          !!toolSpan.errorInfo ||
          (toolSpan.type === SpanType.PROVIDER_TOOL_CALL &&
            (toolSpan.attributes as { success?: boolean } | undefined)?.success === false);
        // Same `tool-result` part shape PostHog's own AI SDK integrations send.
        messages.push({
          role: 'tool',
          content: [
            {
              type: 'tool-result',
              toolCallId: toolCall.toolCallId,
              toolName: toolCall.toolName,
              output: toolSpan.errorInfo ? toolSpan.errorInfo.message : toolSpan.output,
              ...(isError ? { isError: true } : {}),
            },
          ],
        });
      }
    }

    return { ...span, input: messages };
  }

  /** Record ended calls and tool results so later calls in the same generation can rebuild their input. */
  private recordForConversation(span: AnyExportedSpan, traceData: PosthogTraceData): void {
    const isCall = span.type === SpanType.MODEL_INFERENCE && !span.errorInfo;
    // A PROVIDER_TOOL_CALL span ended without a result (e.g. the run stopped first)
    // has no `success`, and recording it would add an empty result.
    const isProviderToolWithResult =
      span.type === SpanType.PROVIDER_TOOL_CALL &&
      typeof (span.attributes as { success?: unknown } | undefined)?.success === 'boolean';
    const isTool = span.type === SpanType.TOOL_CALL || span.type === SpanType.MCP_TOOL_CALL || isProviderToolWithResult;
    if (!isModelInferenceEnabled() || (!isCall && !isTool)) return;

    const generation = this.findGeneration(span, traceData);
    if (!generation) return;

    const key = CONVERSATION + generation.id;
    let conversation = traceData.getExtraValue(key) as Conversation | undefined;
    if (!conversation) {
      conversation = { calls: [], toolResults: new Map() };
      traceData.setExtraValue(key, conversation);
    }
    if (isCall) {
      conversation.calls.push(span);
    } else {
      const toolCallId = this.resolveToolCallId(span);
      if (toolCallId) conversation.toolResults.set(toolCallId, span);
    }
  }

  private findGeneration(span: AnyExportedSpan, traceData: PosthogTraceData): AnyExportedSpan | undefined {
    let ancestor = span.parentSpanId ? traceData.getSpan({ spanId: span.parentSpanId }) : undefined;
    while (ancestor && ancestor.type !== SpanType.MODEL_GENERATION) {
      ancestor = ancestor.parentSpanId ? traceData.getSpan({ spanId: ancestor.parentSpanId }) : undefined;
    }
    return ancestor;
  }

  private resolveToolCallId(span: AnyExportedSpan): string | undefined {
    const attrs = span.attributes as { toolCallId?: string } | undefined;
    return (
      attrs?.toolCallId ??
      (span.metadata?.toolCallId as string | undefined) ??
      (span.input as { toolCallId?: string } | undefined)?.toolCallId
    );
  }

  private getDistinctId(span: AnyExportedSpan, traceData?: PosthogTraceData): string {
    if (span.metadata?.userId) {
      return String(span.metadata.userId);
    }

    if (traceData?.hasExtraValue(DISTINCT_ID)) {
      return String(traceData.getExtraValue(DISTINCT_ID));
    }

    if (this.config.defaultDistinctId) {
      return this.config.defaultDistinctId;
    }

    return 'anonymous';
  }

  /**
   * Check if the parent of this span is the root span.
   * We need this because we don't create $ai_span for root spans,
   * so children of root spans should use $ai_trace_id as their $ai_parent_id.
   */
  private isParentRootSpan(span: AnyExportedSpan, traceData: PosthogTraceData): boolean {
    if (!span.parentSpanId) {
      return false;
    }

    // Look up the parent span in our cache to check if it's a root span
    const parentCache = traceData.getSpan({ spanId: span.parentSpanId });
    if (parentCache) {
      return parentCache.isRootSpan;
    }

    // Parent not found in cache - shouldn't happen normally, but default to false
    return false;
  }

  private buildEventProperties(
    span: AnyExportedSpan,
    latency: number,
    parentIsRootSpan: boolean = false,
    isModelCall: boolean = this.isModelCall(span),
  ): Record<string, any> {
    const baseProperties: Record<string, any> = {
      $ai_trace_id: span.traceId,
      $ai_latency: latency,
      $ai_is_error: !!span.errorInfo,
    };

    if (span.parentSpanId) {
      // If parent is the root span, use trace_id as parent_id since we don't
      // create an $ai_span for root spans (only $ai_trace)
      baseProperties.$ai_parent_id = parentIsRootSpan ? span.traceId : span.parentSpanId;
    }

    if (span.metadata?.sessionId) {
      baseProperties.$ai_session_id = span.metadata.sessionId;
    }

    // Include tags for root spans (tags are only set on root spans by design)
    // PostHog doesn't allow setting tags directly, so we iterate through each tag
    // and set it as a property with value true
    if (span.isRootSpan && span.tags?.length) {
      for (const tag of span.tags) {
        baseProperties[tag] = true;
      }
    }

    if (isModelCall) {
      baseProperties.$ai_generation_id = span.id;
      return { ...baseProperties, ...this.buildGenerationProperties(span) };
    } else {
      baseProperties.$ai_span_id = span.id;
      baseProperties.$ai_span_name = span.name;
      return { ...baseProperties, ...this.buildSpanProperties(span) };
    }
  }

  private extractErrorProperties(errorInfo?: SpanErrorInfo): Record<string, any> {
    if (!errorInfo) {
      return {};
    }

    const props: Record<string, string> = {
      error_message: errorInfo.message,
    };

    if (errorInfo.id) {
      props.error_id = errorInfo.id;
    }

    if (errorInfo.category) {
      props.error_category = errorInfo.category;
    }

    return props;
  }

  private extractCustomMetadata(metadata?: Record<string, unknown>): Record<string, any> {
    const { userId, sessionId, ...customMetadata } = metadata ?? {};
    return customMetadata;
  }

  private buildGenerationProperties(span: AnyExportedSpan): Record<string, any> {
    const props: Record<string, any> = {};
    const attrs = (span.attributes ?? {}) as ModelGenerationAttributes;

    props.$ai_model = attrs.model || 'unknown-model';
    props.$ai_provider = attrs.provider || 'unknown-provider';

    if (span.input) props.$ai_input = this.formatMessages(span.input, 'user');
    if (span.output) props.$ai_output_choices = this.formatMessages(span.output, 'assistant');

    // Extract usage properties using the shared utility
    Object.assign(props, formatUsageMetrics(attrs.usage));

    if (attrs.parameters) {
      if (attrs.parameters.temperature !== undefined) props.$ai_temperature = attrs.parameters.temperature;
      if (attrs.parameters.maxOutputTokens !== undefined) props.$ai_max_tokens = attrs.parameters.maxOutputTokens;
    }
    if (attrs.streaming !== undefined) props.$ai_stream = attrs.streaming;
    if (attrs.tools?.length) {
      // OpenAI-style shape — the format PostHog's own AI SDKs send and its
      // trace view renders. Provider-defined tools pass through as-is.
      props.$ai_tools = attrs.tools.map(tool =>
        tool.type === 'function'
          ? {
              type: 'function',
              function: { name: tool.name, description: tool.description, parameters: tool.parameters },
            }
          : tool,
      );
    }

    return { ...props, ...this.extractErrorProperties(span.errorInfo), ...this.extractCustomMetadata(span.metadata) };
  }

  private buildSpanProperties(span: AnyExportedSpan): Record<string, any> {
    const props: Record<string, any> = {};

    if (span.input) props.$ai_input_state = span.input;
    if (span.output) props.$ai_output_state = span.output;

    if (span.type === SpanType.MODEL_CHUNK) {
      const attrs = span.attributes as any;
      if (attrs?.chunkType) props.chunk_type = attrs.chunkType;
      if (attrs?.sequenceNumber !== undefined) props.chunk_sequence_number = attrs.sequenceNumber;
    }

    if (span.attributes) {
      Object.assign(props, span.attributes);
    }

    return { ...props, ...this.extractErrorProperties(span.errorInfo), ...this.extractCustomMetadata(span.metadata) };
  }

  private formatMessages(data: SpanData, defaultRole: 'user' | 'assistant' = 'user'): PostHogMessage[] {
    // Unwrap {messages: [...]} wrapper produced by generation span inputs
    if (typeof data === 'object' && data !== null && !Array.isArray(data) && 'messages' in data) {
      const wrapped = (data as Record<string, unknown>).messages;
      if (this.isMessageArray(wrapped)) {
        return wrapped.map(msg => this.normalizeMessage(msg));
      }
    }

    if (this.isMessageArray(data)) {
      return data.map(msg => this.normalizeMessage(msg));
    }

    if (typeof data === 'string') {
      return [{ role: defaultRole, content: [{ type: 'text', text: data }] }];
    }

    if (this.isSpanOutputWithToolCalls(data)) {
      const content: PostHogContent[] = [];
      if (data.text) {
        content.push({ type: 'text', text: data.text });
      }
      for (const tc of data.toolCalls) {
        content.push({
          type: 'tool-call',
          id: tc.toolCallId,
          function: { name: tc.toolName, arguments: tc.args },
        });
      }
      return [{ role: 'assistant', content }];
    }

    // Extract text from output objects (e.g. generation outputs with text but no tool calls)
    if (typeof data === 'object' && data !== null && !Array.isArray(data) && 'text' in data) {
      const text = (data as Record<string, unknown>).text;
      if (typeof text === 'string') {
        return [{ role: defaultRole, content: [{ type: 'text', text }] }];
      }
    }

    return [{ role: defaultRole, content: [{ type: 'text', text: this.safeStringify(data) }] }];
  }

  private isSpanOutputWithToolCalls(
    data: unknown,
  ): data is { text?: string; toolCalls: Array<{ toolCallId: string; toolName: string; args: unknown }> } {
    if (typeof data !== 'object' || data === null || !('toolCalls' in data)) return false;
    const { toolCalls } = data as Record<string, unknown>;
    return Array.isArray(toolCalls) && toolCalls.length > 0;
  }

  private isMessageArray(data: unknown): data is MastraMessage[] {
    if (!Array.isArray(data)) {
      return false;
    }

    return data.every(item => typeof item === 'object' && item !== null && 'role' in item && 'content' in item);
  }

  private normalizeMessage(msg: MastraMessage): PostHogMessage {
    if (typeof msg.content === 'string') {
      return {
        role: msg.role as PostHogMessage['role'],
        content: [{ type: 'text', text: msg.content }],
      };
    }

    return {
      role: msg.role as PostHogMessage['role'],
      content: msg.content as PostHogContent[],
    };
  }

  private safeStringify(data: unknown): string {
    try {
      return JSON.stringify(data);
    } catch {
      if (typeof data === 'object' && data !== null) {
        return `[Non-serializable ${data.constructor?.name || 'Object'}]`;
      }
      return String(data);
    }
  }

  /**
   * Force flush any buffered data to PostHog without shutting down.
   */
  protected override async _flush(): Promise<void> {
    if (this.#client) {
      await this.#client.flush();
    }
  }

  override async _postShutdown(): Promise<void> {
    if (this.#client) {
      await this.#client.shutdown();
    }
  }
}
